import { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, X, ChevronRight } from 'lucide-react';
import { useApi } from '../../../hooks/useApi';
import { useAuth } from '../../../contexts/AuthContext';

export const YearVerificationAlert = () => {
  const { user, checkAuth } = useAuth();
  const { put } = useApi();
  const [isVisible, setIsVisible] = useState(false);
  const [calculatedYear, setCalculatedYear] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.role !== 'student' || !user?.studentInfo?.enrollmentYear) return;

    const checkVerificationNeeded = () => {
      // 1. Calculate Expected Year
      const currentYear = new Date().getFullYear();
      const enrollmentYear = parseInt(user.studentInfo.enrollmentYear);
      if (isNaN(enrollmentYear)) return;

      let expectedYear = currentYear - enrollmentYear + 1;
      
      // Basic business logic: undergraduate is 1-4, anything else is custom or graduate
      // But we use the numbers for now as per enrollment logic
      if (expectedYear < 1) expectedYear = 1;
      if (expectedYear > 6) expectedYear = 6; 

      setCalculatedYear(String(expectedYear));

      const storedYear = user.studentInfo.yearOfStudy;

      // 2. Check logic for showing the alert
      // Show if:
      // a) The calculated year is different from the stored year
      // b) It's been more than 9 months since last verification (mandatory update)
      
      const lastVerified = user.studentInfo.lastYearVerification 
        ? new Date(user.studentInfo.lastYearVerification) 
        : null;
      
      const nineMonthsInMs = 9 * 30 * 24 * 60 * 60 * 1000;
      const needsRoutineVerification = !lastVerified || (new Date() - lastVerified) > nineMonthsInMs;
      const isMismatch = String(storedYear) !== String(expectedYear);

      if (isMismatch || needsRoutineVerification) {
        setIsVisible(true);
      }
    };

    checkVerificationNeeded();
  }, [user]);

  const handleUpdateYear = async (year) => {
    setLoading(true);
    try {
      const response = await put('/auth/verify-year', { yearOfStudy: String(year) });
      if (response.data.status === 'success') {
        // Refresh user in context
        await checkAuth();
        setIsVisible(false);
      }
    } catch (error) {
      console.error('Failed to verify year:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isVisible) return null;

  const getYearName = (y) => {
    const yearNames = {
      '1': 'Freshman',
      '2': 'Sophomore',
      '3': 'Junior',
      '4': 'Senior',
      '5': 'Graduate',
      '6': 'Alumni'
    };
    return yearNames[y] || `Year ${y}`;
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-amber-200 dark:border-amber-900/50 rounded-2xl shadow-sm overflow-hidden mb-6 animate-in fade-in slide-in-from-top-4 duration-500">
      <div className="flex flex-col md:flex-row items-stretch">
        <div className="bg-amber-500 w-full md:w-2 shrink-0"></div>
        <div className="p-6 flex-1 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-xl text-amber-600 dark:text-amber-400">
              <AlertTriangle className="h-6 w-6" />
            </div>
            <div>
              <h4 className="font-bold text-gray-900 dark:text-white">Academic Year Verification</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-lg">
                Based on your enrollment year ({user.studentInfo.enrollmentYear}), you should be in 
                <span className="font-bold text-amber-600 dark:text-amber-400 mx-1">
                  {getYearName(calculatedYear)} ({calculatedYear})
                </span>. 
                Your profile currently says <span className="font-bold text-gray-900 dark:text-white mx-1">{getYearName(user.studentInfo.yearOfStudy)}</span>.
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-500 mt-2 font-medium flex items-center gap-1">
                Help us keep your records accurate by confirming your current year of study.
              </p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">
            <button
              onClick={() => handleUpdateYear(calculatedYear)}
              disabled={loading}
              className="w-full sm:w-auto px-6 py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-xl shadow-md shadow-amber-600/20 transition-all flex items-center justify-center gap-2 group"
            >
              {loading ? (
                <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Confirm {getYearName(calculatedYear)}
                </>
              )}
            </button>
            <button
              onClick={() => handleUpdateYear(user.studentInfo.yearOfStudy)}
              disabled={loading}
              className="w-full sm:w-auto px-6 py-2.5 bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 text-sm font-semibold rounded-xl transition-all"
            >
              Still in {getYearName(user.studentInfo.yearOfStudy)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
